import { AxiosResponse } from 'axios';

import { builderInstance } from 'api/base';
import { normalizeBaseGist, normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import { IAllPageFilters, IBaseGist, INullable, IPageType, TObject } from 'utils/types';

import {
  ICheckStatusData,
  IHandleWebhookData,
  IPayoutData,
  ISendPaymentData,
  normalizeAdapter,
  normalizeCheckStatus,
  normalizeHandleWebhook,
  normalizePayout,
  normalizeSendPayment,
} from './normalizers';
import {
  IAdapter,
  IAdapterForm,
  ICheckStatus,
  IHandleWebhook,
  IOperationFormPayload,
  IPayout,
  ISendPayment,
} from './types';

const integrationBuilderAPI = {
  async getAllAdapters(filters: IAllPageFilters): Promise<IPageType<IAdapter>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const { data } = await builderInstance.get('adapter-code', {
      params: normalizedFilter,
    });

    return normalizePageData<TObject, IAdapter>(
      data,
      normalizeAdapter,
      JSON.stringify(filters.search),
    );
  },

  async getAdapter(code: string): Promise<IAdapter> {
    const response = await builderInstance.get(`adapter-code/${code}`);

    return normalizeAdapter(response.data);
  },

  async createAdapter(payload: IAdapterForm): Promise<IAdapter> {
    const normalizedPayload = prepareFormData(payload);

    const response = await builderInstance.post(`adapter-code`, normalizedPayload);

    return normalizeAdapter(response.data);
  },

  async updateAdapter({ code, ...payload }: IAdapterForm): Promise<IAdapter> {
    const normalizedPayload = prepareFormData(payload);

    const response = await builderInstance.put(`adapter-code/${code}`, normalizedPayload);

    return normalizeAdapter(response.data);
  },

  async deleteAdapter(code: string): Promise<AxiosResponse> {
    const response = await builderInstance.delete(`adapter-code/${code}`);

    return response.data;
  },

  async getAllSendPayments(filters: IAllPageFilters): Promise<IPageType<ISendPayment>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const { data } = await builderInstance.get('send-payment', {
      params: normalizedFilter,
    });

    return normalizePageData<ISendPaymentData, ISendPayment>(
      data,
      normalizeSendPayment,
      JSON.stringify(filters.search),
    );
  },

  async createSendPayment(payload: IOperationFormPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await builderInstance.post(`send-payment`, normalizedPayload);

    return normalizeBaseGist(response.data);
  },

  async getSendPayment(id: number | string): Promise<ISendPayment> {
    const response = await builderInstance.get(`send-payment/${id}`);

    return normalizeSendPayment(response.data);
  },

  async updateSendPayment({ id, ...payload }: IOperationFormPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await builderInstance.put(`send-payment/${id}`, normalizedPayload);

    return normalizeBaseGist(response.data);
  },

  async deleteSendPayment(id: number): Promise<AxiosResponse> {
    const response = await builderInstance.delete(`send-payment/${id}`);

    return response.data;
  },

  async getAllHandleWebhook(
    filters: IAllPageFilters,
  ): Promise<IPageType<IHandleWebhook>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const { data } = await builderInstance.get('webhook', {
      params: normalizedFilter,
    });

    return normalizePageData<IHandleWebhookData, IHandleWebhook>(
      data,
      normalizeHandleWebhook,
      JSON.stringify(filters.search),
    );
  },

  async getHandleWebhook(id: number | string): Promise<IHandleWebhook> {
    const response = await builderInstance.get(`webhook/${id}`);

    return normalizeHandleWebhook(response.data);
  },

  async createHandleWebhook(payload: IOperationFormPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await builderInstance.post(`webhook`, normalizedPayload);

    return normalizeBaseGist(response.data);
  },

  async updateHandleWebhook({
    id,
    ...payload
  }: IOperationFormPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await builderInstance.put(`webhook/${id}`, normalizedPayload);

    return normalizeBaseGist(response.data);
  },

  async deleteHandleWebhook(id: INullable<number>): Promise<AxiosResponse> {
    const response = await builderInstance.delete(`webhook/${id}`);

    return response;
  },

  async getAllPayout(filters: IAllPageFilters): Promise<IPageType<IPayout>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const { data } = await builderInstance.get('payout', {
      params: normalizedFilter,
    });

    return normalizePageData<IPayoutData, IPayout>(
      data,
      normalizePayout,
      JSON.stringify(filters.search),
    );
  },

  async getPayout(id: number | string): Promise<IPayout> {
    const response = await builderInstance.get(`payout/${id}`);

    return normalizePayout(response.data);
  },

  async createPayout(payload: IOperationFormPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await builderInstance.post(`payout`, normalizedPayload);

    return normalizeBaseGist(response.data);
  },

  async updatePayout({ id, ...payload }: IOperationFormPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await builderInstance.put(`payout/${id}`, normalizedPayload);

    return normalizeBaseGist(response.data);
  },

  async deletePayout(id: INullable<number>): Promise<AxiosResponse> {
    const response = await builderInstance.delete(`payout/${id}`);

    return response;
  },

  async getAllCheckStatus(filters: IAllPageFilters): Promise<IPageType<ICheckStatus>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const { data } = await builderInstance.get('check-status', {
      params: normalizedFilter,
    });

    return normalizePageData<ICheckStatusData, ICheckStatus>(
      data,
      normalizeCheckStatus,
      JSON.stringify(filters.search),
    );
  },

  async getCheckStatus(id: number | string): Promise<ICheckStatus> {
    const response = await builderInstance.get(`check-status/${id}`);

    return normalizeCheckStatus(response.data);
  },

  async createCheckStatus(payload: IOperationFormPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await builderInstance.post(`check-status`, normalizedPayload);

    return normalizeBaseGist(response.data);
  },

  async updateCheckStatus({ id, ...payload }: IOperationFormPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await builderInstance.put(`check-status/${id}`, normalizedPayload);

    return normalizeBaseGist(response.data);
  },

  async deleteCheckStatus(id: INullable<number>): Promise<AxiosResponse> {
    const response = await builderInstance.delete(`check-status/${id}`);

    return response;
  },
};

export default integrationBuilderAPI;
